import { useEffect, useState } from "react";
import { api, fmtNum, fmtIDR } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import {
  ShoppingCart, Plus, RefreshCw, Trash2, Star, Users, Package,
  MessageSquare, ExternalLink, TrendingUp, TrendingDown, Clock, AlertCircle, X, Sparkles, Split, LineChart as LineChartIcon
} from "lucide-react";

const relativeTime = (iso) => {
  if (!iso) return "-";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "baru saja";
  if (diff < 3600) return `${Math.floor(diff/60)} menit lalu`;
  if (diff < 86400) return `${Math.floor(diff/3600)} jam lalu`;
  return `${Math.floor(diff/86400)} hari lalu`;
};

export default function Competitors() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "marketing_manager";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ url: "", note: "" });
  const [expanded, setExpanded] = useState(null);
  const [histories, setHistories] = useState({}); // { comp_id: { history, delta } }
  const [aiModal, setAiModal] = useState(null); // { comp, analysis, loading }
  const [compareModal, setCompareModal] = useState(null); // { comp, data }

  const load = async () => {
    try {
      const { data } = await api.get("/competitors");
      setItems(data.competitors || []);
      // Preload histories for all
      data.competitors?.forEach(c => {
        api.get(`/competitors/${c.id}/history`, { params: { days: 30 } })
          .then(r => setHistories(prev => ({ ...prev, [c.id]: r.data })))
          .catch(() => {});
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/competitors", form);
      toast.success("Kompetitor ditambahkan");
      setShowAdd(false);
      setForm({ url: "", note: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal tambah kompetitor");
    } finally { setBusy(false); }
  };

  const refresh = async (c) => {
    setBusy(true);
    try {
      await api.post(`/competitors/${c.id}/refresh`);
      toast.success(`Data ${c.name} disegarkan`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh gagal");
    } finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Hapus kompetitor "${c.name}"?`)) return;
    await api.delete(`/competitors/${c.id}`);
    toast.success("Kompetitor dihapus");
    load();
  };

  const openAI = async (c) => {
    setAiModal({ comp: c, loading: true, analysis: "" });
    try {
      const { data } = await api.post(`/competitors/${c.id}/ai-analysis`);
      setAiModal({ comp: c, loading: false, analysis: data.analysis, generated_at: data.generated_at });
    } catch (err) {
      setAiModal({ comp: c, loading: false, analysis: "", error: err?.response?.data?.detail || "AI analysis gagal" });
    }
  };

  const openCompare = async (c) => {
    setCompareModal({ comp: c, loading: true });
    try {
      const { data } = await api.get("/competitors/compare/data", { params: { competitor_id: c.id } });
      setCompareModal({ comp: c, loading: false, data });
    } catch (err) {
      toast.error("Gagal load compare data");
      setCompareModal(null);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 fade-in">
      <div className="pb-2 border-b border-[#1F1F1F] flex items-end justify-between">
        <div>
          <div className="label-mono">// competitive intelligence · shopee</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2" data-testid="competitors-title">
            Kompetitor <span className="text-[#00F0FF]">Shopee</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-2">Pantau performa toko kompetitor dari data publik Shopee — follower, rating, produk terlaris, response rate.</p>
        </div>
        {canManage && (
          <button onClick={() => setShowAdd(true)}
            data-testid="competitors-add-btn"
            className="bg-[#007AFF] hover:bg-[#0056b3] px-4 py-2.5 label-mono flex items-center gap-2">
            <Plus size={12} /> tambah kompetitor
          </button>
        )}
      </div>

      {loading ? (
        <div className="label-mono text-zinc-500"><span className="terminal-cursor">memuat</span></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#1F1F1F] p-16 text-center">
          <ShoppingCart size={40} className="mx-auto text-zinc-700 mb-4" />
          <h3 className="font-display font-bold text-xl mb-2">Belum ada kompetitor</h3>
          <p className="text-zinc-500 text-sm mb-6">Mulai pantau kompetitor Anda dengan menambahkan URL toko Shopee mereka.</p>
          {canManage && (
            <button onClick={() => setShowAdd(true)}
              data-testid="competitors-add-empty-btn"
              className="bg-[#007AFF] hover:bg-[#0056b3] px-5 py-2.5 label-mono">+ tambah kompetitor pertama</button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((c) => {
            const s = c.snapshot || {};
            const isExpanded = expanded === c.id;
            return (
              <div key={c.id} className="border border-[#1F1F1F] bg-[#0A0A0A]" data-testid={`competitor-${c.username}`}>
                <div className="px-5 py-4 border-b border-[#1F1F1F] flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 border border-[#1F1F1F] bg-[#050505] flex items-center justify-center shrink-0">
                      <ShoppingCart size={18} className="text-[#EE4D2D]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{c.name}</span>
                        {s.is_official_shop && <span className="label-mono text-[#EE4D2D]">official</span>}
                        {s.is_shopee_verified && <span className="label-mono text-[#00E676]">verified</span>}
                      </div>
                      <div className="label-mono truncate">@{c.username}</div>
                      {c.note && <div className="text-xs text-zinc-500 mt-1 italic">{c.note}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a href={c.url} target="_blank" rel="noreferrer" title="Buka toko"
                      className="p-2 border border-[#1F1F1F] hover:border-[#00F0FF] hover:text-[#00F0FF] label-mono">
                      <ExternalLink size={12} />
                    </a>
                    {canManage && (
                      <>
                        <button onClick={() => refresh(c)} disabled={busy} title="Refresh data"
                          data-testid={`competitor-refresh-${c.username}`}
                          className="p-2 border border-[#1F1F1F] hover:border-[#007AFF] hover:text-[#007AFF] label-mono">
                          <RefreshCw size={12} />
                        </button>
                        <button onClick={() => del(c)} title="Hapus"
                          data-testid={`competitor-delete-${c.username}`}
                          className="p-2 border border-[#1F1F1F] hover:border-[#FF3B30] hover:text-[#FF3B30] label-mono">
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {c.last_error && !s.follower_count ? (
                  <div className="p-5 border-l-4 border-l-[#FF3B30] bg-[#FF3B30]/5">
                    <div className="flex items-center gap-2 label-mono text-[#FF3B30] mb-2"><AlertCircle size={12} /> data tidak tersedia</div>
                    <div className="text-xs text-zinc-400 font-mono break-words">{c.last_error}</div>
                  </div>
                ) : (
                  <>
                    {c.last_error && (
                      <div className="px-5 py-2 border-b border-[#1F1F1F] bg-[#FFCC00]/5 flex items-center gap-2 label-mono text-[#FFCC00]">
                        <AlertCircle size={10} /> refresh terakhir gagal ({c.last_error.slice(0, 50)}) — data mungkin outdated
                      </div>
                    )}
                    <div className="grid grid-cols-4 gap-0 border-b border-[#1F1F1F]">
                      <div className="p-4 border-r border-[#1F1F1F]">
                        <div className="label-mono flex items-center gap-1"><Users size={10} /> follower</div>
                        <div className="font-mono text-lg font-medium mt-1">{fmtNum(s.follower_count)}</div>
                        {histories[c.id]?.delta?.follower_delta !== undefined && (
                          <div className={`label-mono mt-1 flex items-center gap-1 ${histories[c.id].delta.follower_delta >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                            {histories[c.id].delta.follower_delta >= 0 ? <TrendingUp size={9}/> : <TrendingDown size={9}/>}
                            {histories[c.id].delta.follower_delta >= 0 ? "+" : ""}{fmtNum(histories[c.id].delta.follower_delta)} · {histories[c.id].delta.days_covered}h
                          </div>
                        )}
                      </div>
                      <div className="p-4 border-r border-[#1F1F1F]">
                        <div className="label-mono flex items-center gap-1"><Package size={10} /> produk</div>
                        <div className="font-mono text-lg font-medium mt-1">{fmtNum(s.item_count)}</div>
                      </div>
                      <div className="p-4 border-r border-[#1F1F1F]">
                        <div className="label-mono flex items-center gap-1"><Star size={10} /> rating</div>
                        <div className="font-mono text-lg font-medium mt-1 text-[#FFCC00]">
                          {s.rating_star ? Number(s.rating_star).toFixed(1) : "-"}
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="label-mono flex items-center gap-1"><MessageSquare size={10} /> respons</div>
                        <div className="font-mono text-lg font-medium mt-1 text-[#00E676]">
                          {s.response_rate ? `${s.response_rate}%` : "-"}
                        </div>
                      </div>
                    </div>

                    {histories[c.id]?.history?.length > 1 && (
                      <div className="px-5 py-3 border-b border-[#1F1F1F] bg-[#050505]">
                        <div className="label-mono mb-2 flex items-center gap-1"><LineChartIcon size={10} /> tren follower {histories[c.id].delta.days_covered} hari</div>
                        <ResponsiveContainer width="100%" height={60}>
                          <LineChart data={histories[c.id].history}>
                            <XAxis dataKey="date" hide />
                            <YAxis hide domain={["dataMin", "dataMax"]} />
                            <Tooltip contentStyle={{ background: "#050505", border: "1px solid #1F1F1F", fontSize: 11, fontFamily: "IBM Plex Mono" }}
                              formatter={(v) => fmtNum(v)} labelFormatter={(l) => l} />
                            <Line type="monotone" dataKey="follower_count" stroke="#00F0FF" strokeWidth={1.5} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    <div className="px-5 py-2 border-b border-[#1F1F1F] flex items-center gap-2">
                      <button onClick={() => openAI(c)}
                        data-testid={`competitor-ai-${c.username}`}
                        className="flex-1 label-mono px-3 py-2 border border-[#1F1F1F] hover:border-[#00F0FF] hover:text-[#00F0FF] flex items-center justify-center gap-2">
                        <Sparkles size={12} /> ai insight
                      </button>
                      <button onClick={() => openCompare(c)}
                        data-testid={`competitor-compare-${c.username}`}
                        className="flex-1 label-mono px-3 py-2 border border-[#1F1F1F] hover:border-[#B180FF] hover:text-[#B180FF] flex items-center justify-center gap-2">
                        <Split size={12} /> bandingkan
                      </button>
                    </div>

                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="label-mono">// produk terlaris (top {(c.products || []).length})</div>
                        <button onClick={() => setExpanded(isExpanded ? null : c.id)}
                          data-testid={`competitor-toggle-${c.username}`}
                          className="label-mono text-[#00F0FF] hover:underline">
                          {isExpanded ? "sembunyikan" : "lihat semua"}
                        </button>
                      </div>
                      {(c.products || []).slice(0, isExpanded ? 8 : 3).map((p, i) => (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-[#1F1F1F] last:border-0">
                          <span className="font-mono text-xs text-zinc-600 w-6 shrink-0">{String(i+1).padStart(2,"0")}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{p.name}</div>
                            <div className="label-mono">
                              {fmtNum(p.historical_sold || p.sold)} terjual · ★ {p.rating_star?.toFixed(1) || "-"}
                            </div>
                          </div>
                          <div className="font-mono text-xs text-[#00E676] shrink-0">{fmtIDR(p.price)}</div>
                        </div>
                      ))}
                      {(c.products || []).length === 0 && (
                        <div className="label-mono text-zinc-600 py-2">tidak ada data produk</div>
                      )}
                    </div>
                  </>
                )}

                <div className="px-5 py-2 border-t border-[#1F1F1F] flex items-center justify-between label-mono text-zinc-600">
                  <span className="flex items-center gap-1"><Clock size={10} /> update: {relativeTime(c.snapshot_at)}</span>
                  {s.shop_location && <span>📍 {s.shop_location}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            data-testid="competitor-add-modal"
            className="w-full max-w-md border border-[#1F1F1F] bg-[#0A0A0A] p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-mono">// track competitor</div>
                <h3 className="font-display font-bold text-2xl mt-1">Tambah Kompetitor</h3>
              </div>
              <button type="button" onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            <div>
              <label className="label-mono block mb-2">url toko shopee atau username</label>
              <input required value={form.url} onChange={(e) => setForm({...form, url: e.target.value})}
                autoFocus
                data-testid="competitor-form-url"
                placeholder="https://shopee.co.id/namatoko atau namatoko"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]" />
              <div className="label-mono mt-2 text-zinc-600">// contoh: https://shopee.co.id/tokopedia_id atau tokopedia_id</div>
            </div>
            <div>
              <label className="label-mono block mb-2">catatan (opsional)</label>
              <input value={form.note} onChange={(e) => setForm({...form, note: e.target.value})}
                data-testid="competitor-form-note"
                placeholder="mis. kompetitor utama kategori fashion"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-[#1F1F1F] label-mono hover:bg-[#0F0F0F]">batal</button>
              <button type="submit" disabled={busy} data-testid="competitor-form-submit"
                className="flex-1 py-2.5 bg-[#007AFF] hover:bg-[#0056b3] label-mono disabled:opacity-50">
                {busy ? "mengambil data..." : "→ tambah"}
              </button>
            </div>
          </form>
        </div>
      )}
      {aiModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setAiModal(null)}>
          <div onClick={(e) => e.stopPropagation()}
            data-testid="competitor-ai-modal"
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-[#1F1F1F] bg-[#0A0A0A] p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-mono flex items-center gap-1"><Sparkles size={10} className="text-[#00F0FF]" /> ai competitive analysis</div>
                <h3 className="font-display font-bold text-2xl mt-1">{aiModal.comp.name}</h3>
                <div className="label-mono">@{aiModal.comp.username}</div>
              </div>
              <button onClick={() => setAiModal(null)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            {aiModal.loading ? (
              <div className="py-16 flex flex-col items-center gap-3 text-zinc-500">
                <Sparkles size={32} className="text-[#00F0FF] animate-pulse" />
                <span className="label-mono terminal-cursor">gpt-5.2 menganalisa data</span>
              </div>
            ) : aiModal.error ? (
              <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4 label-mono text-[#FF3B30]">
                {aiModal.error}
              </div>
            ) : (
              <>
                <div className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-200 font-body">{aiModal.analysis}</div>
                <div className="label-mono text-zinc-600 pt-3 border-t border-[#1F1F1F]">
                  // generated: {new Date(aiModal.generated_at).toLocaleString("id-ID")} · powered by gpt-5.2
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {compareModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setCompareModal(null)}>
          <div onClick={(e) => e.stopPropagation()}
            data-testid="competitor-compare-modal"
            className="w-full max-w-3xl border border-[#1F1F1F] bg-[#0A0A0A] p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-mono flex items-center gap-1"><Split size={10} className="text-[#B180FF]" /> side-by-side compare</div>
                <h3 className="font-display font-bold text-2xl mt-1">Toko Anda vs {compareModal.comp.name}</h3>
              </div>
              <button onClick={() => setCompareModal(null)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            {compareModal.loading ? (
              <div className="label-mono text-zinc-500 py-6"><span className="terminal-cursor">memuat compare data</span></div>
            ) : compareModal.data ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-[#1F1F1F] bg-[#050505] p-4 space-y-3">
                  <div className="label-mono text-[#00F0FF]">// toko anda</div>
                  <div className="font-medium">{compareModal.data.us?.channel || "Belum ada channel Shopee aktif"}</div>
                  {compareModal.data.us && (
                    <div className="space-y-2 font-mono text-sm">
                      <div className="flex justify-between"><span className="text-zinc-500">order (30 hari)</span><span>{fmtNum(compareModal.data.us.orders)}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">revenue</span><span className="text-[#00E676]">{fmtIDR(compareModal.data.us.revenue)}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">rating</span><span className="text-[#FFCC00]">★ {compareModal.data.us.rating}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">total review</span><span>{fmtNum(compareModal.data.us.reviews_total)}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-500">growth</span><span className={compareModal.data.us.growth_pct >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}>{compareModal.data.us.growth_pct >= 0 ? "+" : ""}{compareModal.data.us.growth_pct}%</span></div>
                    </div>
                  )}
                </div>
                <div className="border border-[#1F1F1F] bg-[#050505] p-4 space-y-3">
                  <div className="label-mono text-[#EE4D2D]">// {compareModal.data.competitor.name} {compareModal.data.competitor.is_official && "· OFFICIAL"}</div>
                  <div className="font-medium">@{compareModal.data.competitor.username}</div>
                  <div className="space-y-2 font-mono text-sm">
                    <div className="flex justify-between"><span className="text-zinc-500">follower</span><span>{fmtNum(compareModal.data.competitor.followers)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">jumlah produk</span><span>{fmtNum(compareModal.data.competitor.items)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">rating</span><span className="text-[#FFCC00]">★ {Number(compareModal.data.competitor.rating).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">response rate</span><span className="text-[#00E676]">{compareModal.data.competitor.response_rate}%</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">lokasi</span><span className="text-xs">{compareModal.data.competitor.shop_location || "-"}</span></div>
                    {compareModal.data.competitor.top_product && (
                      <div className="pt-2 border-t border-[#1F1F1F]">
                        <div className="label-mono text-zinc-600">top product</div>
                        <div className="text-xs mt-1 truncate">{compareModal.data.competitor.top_product}</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2 label-mono text-zinc-600 pt-2 border-t border-[#1F1F1F]">
                  // klik "ai insight" pada card untuk analisa GPT-5.2 kenapa mereka bertumbuh & rekomendasi strategi
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
